import React, { useEffect, useState, useMemo } from 'react';
import LayoutHome from '../../../components/layout/LayoutHome';
import { Button, Card, InputNumber, Typography, Spin, Alert } from 'antd';
import { RocketOutlined, ClockCircleOutlined, ShopOutlined, CarOutlined } from '@ant-design/icons';
import { IoCashOutline } from "react-icons/io5";
import { useGetBodegas } from '../../../hooks/useBodegas';
import { useGetUsuarios } from '../../../hooks/useUsuarios';
import { useAuth } from '../../../aws/AuthContext';

const { Title, Text } = Typography;

const VentanaAbrirCaja = ({ setBaseCaja, handleAbrirCaja }) => {
    const { getJwtToken } = useAuth();
    const [fechaActual, setFechaActual] = useState(new Date());
    
    // Variables para guardar los datos extraídos del Token de AWS
    const [tokenSub, setTokenSub] = useState(null);
    const [tokenEmail, setTokenEmail] = useState(null);

    const { data: bodegas = [], isLoading: cargandoBodegas } = useGetBodegas();
    const { data: usuarios = [], isLoading: cargandoUsr } = useGetUsuarios();

    useEffect(() => {
        const timer = setInterval(() => setFechaActual(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    // 1. EXTRAEMOS EL EMAIL Y EL SUB DIRECTAMENTE DEL TOKEN
    useEffect(() => {
        const fetchSub = async () => {
            try {
                const token = await getJwtToken();
                if (token) {
                    const base64Url = token.split('.')[1];
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    }).join(''));
                    
                    const payload = JSON.parse(jsonPayload);
                    setTokenSub(payload.sub); 
                    setTokenEmail(payload.email); // Extraemos el correo (ej: andresredes46@gmail.com)
                }
            } catch (error) {
                console.error("Error decodificando token", error);
            }
        };
        fetchSub();
    }, [getJwtToken]);

    // 2. CRUZAMOS LAS TABLAS USANDO EL EMAIL
    const bodegaDetectada = useMemo(() => {
        if (bodegas.length === 0 || usuarios.length === 0 || !tokenSub) return null;

        // Buscamos al usuario usando el EMAIL o el SUB (por si acaso la API lo envía)
        const usuarioFisico = usuarios.find(u => 
            (tokenEmail && u.email && String(u.email).toLowerCase() === String(tokenEmail).toLowerCase()) || 
            (u.cognito_sub && String(u.cognito_sub) === String(tokenSub))
        );
        
        // Extraemos su ID interno de la base de datos (4b3610ae-6056-487b-9a51-d2ee3a82a785)
        const idInterno = usuarioFisico ? String(usuarioFisico.id) : null;

        const bodegaEncontrada = bodegas.find(b => {
            if (!b.activa || b.es_default) return false;
            
            // Si es Vehículo / Ruta
            if (b.tipo === 'vendedor') {
                return String(b.usuario_vendedor_id) === String(idInterno) || String(b.usuario_vendedor_id) === String(tokenSub);
            }
            
            // Si es Bodega Secundaria
            let asignados = [];
            try { asignados = typeof b.usuarios_asignados === 'string' ? JSON.parse(b.usuarios_asignados) : b.usuarios_asignados; } catch(e){}
            return (idInterno && asignados.includes(String(idInterno))) || asignados.includes(String(tokenSub));
        });

        // Retorna la bodega asignada, o la principal por defecto
        return bodegaEncontrada || bodegas.find(b => b.es_default);
    }, [bodegas, usuarios, tokenSub, tokenEmail]);

    return (
        <LayoutHome pageSeleccionada="cierre-caja">
            <div style={{ maxWidth: '480px', margin: '40px auto', padding: '0 20px', animation: 'fadeIn 0.5s' }}>
                <Card bordered={false} style={{ borderRadius: '24px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                    <div style={{ background: '#e0f2fe', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                        <IoCashOutline style={{ fontSize: '40px', color: '#0ea5e9' }} />
                    </div>

                    <Title level={3} style={{ color: '#0f172a', marginBottom: '8px', fontWeight: '800' }}>Apertura de Turno</Title>
                    <Text style={{ fontSize: '15px', color: '#64748b', display: 'block', marginBottom: '20px' }}>Ingresa el efectivo disponible en caja.</Text>

                    {/* INDICADOR VISUAL */}
                    {cargandoBodegas || cargandoUsr || !tokenSub ? <Spin style={{ margin: '15px 0' }}/> : (
                        <Alert 
                            message={`Inventario de: ${bodegaDetectada?.nombre || 'Cargando...'}`}
                            description={bodegaDetectada?.tipo === 'vendedor' ? 'Eres responsable del inventario de esta ruta.' : 'Tus ventas descontarán de este punto.'}
                            type={bodegaDetectada?.tipo === 'vendedor' ? 'warning' : 'info'}
                            showIcon 
                            icon={bodegaDetectada?.tipo === 'vendedor' ? <CarOutlined/> : <ShopOutlined/>}
                            style={{ textAlign: 'left', borderRadius: '12px', marginBottom: '25px', fontWeight: 'bold' }}
                        />
                    )}

                    <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', marginBottom: '32px', border: '1px solid #e2e8f0' }}>
                        <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '13px', color: '#475569', textTransform: 'uppercase' }}>Base Inicial (Efectivo)</Text>
                        <InputNumber
                            size="large" controls={false}
                            style={{ width: '100%', fontSize: '32px', fontWeight: '800', height: '65px', borderRadius: '12px', color: '#0f172a' }}
                            placeholder="$ 0"
                            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                            parser={(value) => value.replace(/\$\s?|(\.*)/g, '')}
                            onChange={(val) => setBaseCaja(val || 0)}
                            autoFocus
                        />
                    </div>

                    <Button
                        type="primary" size="large" block
                        icon={<RocketOutlined style={{ fontSize: '20px' }} />}
                        onClick={() => handleAbrirCaja(bodegaDetectada?.id)}
                        disabled={!bodegaDetectada}
                        style={{ height: '55px', borderRadius: '14px', backgroundColor: '#22BABB', fontWeight: 'bold', fontSize: '16px' }}
                    >
                        INICIAR JORNADA
                    </Button>
                </Card>
            </div>
        </LayoutHome>
    )
}
export default VentanaAbrirCaja;